import { useState } from 'react';
import { Select } from '@base-ui/react/select';
import { Settings2 } from 'lucide-react';
import { BUILT_IN_WATCHLISTS, type UserWatchlist } from '@/lib/watchlists';
import { DropdownChevron } from './icons/DropdownChevron';

interface WatchlistSelectorProps {
  watchlists: readonly UserWatchlist[];
  activeWatchlistId: string;
  activeSymbolCounts: ReadonlyMap<string, number>;
  personalWatchlistsLoaded?: boolean;
  personalWatchlistLoadError?: string | null;
  onWatchlistChange: (watchlistId: string) => void;
  onManage: () => void;
}

const GROUP_LABEL_CLASS_NAME =
  'px-2.5 pb-1 pt-2 text-[10.5px] uppercase tracking-[0.08em] text-dim';

export function WatchlistSelector({
  watchlists,
  activeWatchlistId,
  activeSymbolCounts,
  personalWatchlistsLoaded = true,
  personalWatchlistLoadError = null,
  onWatchlistChange,
  onManage,
}: WatchlistSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const activeName =
    BUILT_IN_WATCHLISTS.find((watchlist) => watchlist.id === activeWatchlistId)?.name ??
    watchlists.find((watchlist) => watchlist.id === activeWatchlistId)?.name ??
    'INDEXES';
  const savedWatchlists = watchlists.filter(
    (watchlist) => (activeSymbolCounts.get(watchlist.id) ?? 0) > 0,
  );

  return (
    <Select.Root
      value={activeWatchlistId}
      open={isOpen}
      onOpenChange={setIsOpen}
      onValueChange={(watchlistId) => {
        if (watchlistId != null) {
          onWatchlistChange(watchlistId);
          setIsOpen(false);
        }
      }}
      modal={false}
    >
      <Select.Trigger
        aria-label="Select watchlist"
        className="flex h-10 w-full min-w-0 items-center justify-between gap-3 rounded-md border border-line-strong bg-panel px-3 font-mono text-[13px] tracking-[0.04em] text-fg hover:bg-panel-2 min-[721px]:w-auto min-[721px]:min-w-[150px]"
      >
        <Select.Value className="truncate">{activeName}</Select.Value>
        <DropdownChevron />
      </Select.Trigger>

      <Select.Portal>
        <Select.Positioner
          sideOffset={6}
          align="start"
          alignItemWithTrigger={false}
          className="z-[70]"
        >
          <Select.Popup
            aria-label="Watchlist options"
            className="w-[250px] rounded-md border leading-[normal] border-line-strong bg-panel-2 p-1 shadow-[0_14px_34px_rgba(0,0,0,0.55)]"
          >
            <Select.List>
              <Select.Group>
                <Select.GroupLabel className={GROUP_LABEL_CLASS_NAME}>Built-in</Select.GroupLabel>
                {BUILT_IN_WATCHLISTS.map((watchlist) => (
                  <WatchlistOption
                    key={watchlist.id}
                    id={watchlist.id}
                    name={watchlist.name}
                    count={activeSymbolCounts.get(watchlist.id) ?? 0}
                  />
                ))}
              </Select.Group>
              {savedWatchlists.length > 0 && (
                <Select.Group>
                  <Select.GroupLabel className={GROUP_LABEL_CLASS_NAME}>Saved</Select.GroupLabel>
                  {savedWatchlists.map((watchlist) => (
                    <WatchlistOption
                      key={watchlist.id}
                      id={watchlist.id}
                      name={watchlist.name}
                      count={activeSymbolCounts.get(watchlist.id) ?? 0}
                    />
                  ))}
                </Select.Group>
              )}
            </Select.List>
            <div className="my-1 border-t border-line" />
            {personalWatchlistsLoaded ? (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onManage();
                }}
                className="flex w-full items-center gap-2 rounded px-2.5 py-[9px] text-left font-mono text-[12.5px] text-subtle hover:bg-white/[.05] hover:text-fg"
              >
                <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
                Manage watchlists
              </button>
            ) : (
              <div className="px-2.5 py-[9px] font-mono text-[12.5px] text-dim">
                {personalWatchlistLoadError == null
                  ? 'Loading saved watchlists...'
                  : 'Saved watchlists unavailable'}
              </div>
            )}
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}

function WatchlistOption({ id, name, count }: { id: string; name: string; count: number }) {
  return (
    <Select.Item
      value={id}
      nativeButton
      render={<button type="button" />}
      tabIndex={-1}
      onPointerUp={(event) => {
        if (event.pointerType === 'touch') {
          event.preventDefault();
          event.currentTarget.click();
        }
      }}
      className="flex w-full cursor-default items-center justify-between gap-2.5 rounded px-2.5 py-[9px] text-left font-mono text-[12.5px] text-subtle data-[highlighted]:bg-white/[.05] data-[highlighted]:text-fg data-[selected]:bg-white/[.08] data-[selected]:text-fg"
    >
      <span className="truncate">{name}</span>
      <span className="text-dim">{count}</span>
    </Select.Item>
  );
}
